/**
 * Turns a store record into the open/closed segments to draw for one weekday.
 *
 * Segments are minutes since midnight on a 0–1440 axis. Anything crossing
 * midnight is split, so a renderer can treat every segment as a simple bar.
 */

import { normaliseOperatingHours } from "./operatingHours";
import type { LebronStore } from "./queries";

export interface Segment {
  startMin: number;
  endMin: number;
}

export interface DaySchedule {
  /**
   * `windows` — open for the given segments.
   * `closed` — explicitly closed that day.
   * `unknown` — hours could not be determined; draw nothing and say so.
   */
  kind: "windows" | "closed" | "unknown";
  segments: Segment[];
  /** Whether these came from a per-day override or the single default window. */
  source: "per-day" | "default";
}

function timeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * One `open`–`close` pair as drawable segments.
 *
 * A close earlier than the open means the store trades past midnight, which
 * becomes two bars. Equal times are read as open around the clock — that is
 * the convention the backend uses for 24-hour sites.
 */
export function splitWindow(
  open: string | null | undefined,
  close: string | null | undefined,
): Segment[] {
  const start = timeToMinutes(open);
  const end = timeToMinutes(close);
  if (start === null || end === null) return [];

  if (start === end) return [{ startMin: 0, endMin: 1440 }];
  if (end > start) return [{ startMin: start, endMin: end }];
  return [
    { startMin: start, endMin: 1440 },
    { startMin: 0, endMin: end },
  ];
}

/**
 * What to draw for `store` on `day`, in the store's own local time.
 *
 * Per-day `operating_hours` win when they name the day. A store whose
 * `operating_hours` omits the day falls back to its default window rather than
 * being shown as closed — a partial override means "these days differ", not
 * "the rest are shut" — and `source` records that so the UI can distinguish it.
 */
export function storeDaySchedule(store: LebronStore, day: string): DaySchedule {
  const hours = normaliseOperatingHours(store.operating_hours);

  if (hours.kind === "parsed") {
    const match = hours.days.find((entry) => entry.day === day);
    if (match) {
      if (match.windows.length === 0) {
        return { kind: "closed", segments: [], source: "per-day" };
      }
      return {
        kind: "windows",
        segments: match.windows.flatMap((w) => splitWindow(w.open, w.close)),
        source: "per-day",
      };
    }
  }

  // Reached when `operating_hours` is absent, unparseable, or simply has no
  // entry for this day. In every one of those cases the store's own
  // `opening_time`/`closing_time` is still good data, so fall back to it —
  // an unreadable override must not hide hours the record plainly has.
  // `unknown` is reserved for a store with no usable times anywhere.
  const segments = splitWindow(store.opening_time, store.closing_time);
  if (segments.length === 0) {
    return { kind: "unknown", segments: [], source: "default" };
  }
  return { kind: "windows", segments, source: "default" };
}

/** Wraps a shifted segment back onto the 0–1440 axis, splitting at midnight. */
function wrapSegment(segment: Segment): Segment[] {
  const length = segment.endMin - segment.startMin;
  if (length <= 0) return [];
  if (length >= 1440) return [{ startMin: 0, endMin: 1440 }];

  const start = ((segment.startMin % 1440) + 1440) % 1440;
  const end = start + length;
  if (end <= 1440) return [{ startMin: start, endMin: end }];
  return [
    { startMin: start, endMin: 1440 },
    { startMin: 0, endMin: end - 1440 },
  ];
}

/**
 * Moves segments by `shiftMin` and re-wraps them onto the day axis.
 *
 * Used to redraw store-local hours on a shared clock: a shifted window can
 * spill past either end of the day, so it comes back as up to two bars.
 */
export function shiftSegments(
  segments: Segment[],
  shiftMin: number,
): Segment[] {
  return segments.flatMap((segment) =>
    wrapSegment({
      startMin: segment.startMin + shiftMin,
      endMin: segment.endMin + shiftMin,
    }),
  );
}

/** Total open minutes across segments, for sorting and summaries. */
export function totalOpenMinutes(segments: Segment[]): number {
  return segments.reduce(
    (sum, segment) => sum + (segment.endMin - segment.startMin),
    0,
  );
}
